import { createRequestFn } from '@saas/api-client';
import type { GlobalStore } from '@/stores/global.store';
import type { CustomerStore } from '@/stores/customer.store';

const customerStoreBridge = {
	getToken: (): string | null => null,
	isAuthenticated: (): boolean => false,
	onLogout: () => {},
	getLanguage: (): string => 'no',
};

export function connectCustomerStoreBridge(store: CustomerStore) {
	customerStoreBridge.getToken = () => store.getState().token;
	customerStoreBridge.isAuthenticated = () => store.getState().authenticated;
	customerStoreBridge.onLogout = () => store.onLogout();
}

/**
 * Language is a single source of truth on the global store (set by
 * `useLanguageHandler`). The customer portal bridges through it so the
 * `Accept-Language` header follows whatever the user selected via `?lang=`.
 */
export function connectCustomerLanguageBridge(store: GlobalStore) {
	customerStoreBridge.getLanguage = () => store.getState().language;
}

function getCustomerToken(): string {
	const token = customerStoreBridge.getToken();
	if (!token) {
		throw new Error('Customer not authenticated');
	}
	return token;
}

export const customerAuthRequestFunction = createRequestFn({
	baseUrl: import.meta.env.VITE_API_URL,
	async onResponse(response) {
		if (response.status === 401 && customerStoreBridge.isAuthenticated()) {
			customerStoreBridge.onLogout();
		}
	},
	async onRequest(_url, init) {
		const headers = new Headers(init.headers);
		headers.set('Accept-Language', customerStoreBridge.getLanguage());

		const token = customerStoreBridge.getToken();
		if (token) headers.set('Authorization', `Bearer ${token}`);
		return { ...init, headers };
	},
});

/**
 * Simple authenticated fetch for customer endpoints not covered by the generated API client.
 */
export async function customerFetch(path: string, init: RequestInit = {}): Promise<Response> {
	const token = getCustomerToken();
	const headers = new Headers(init.headers);
	headers.set('Authorization', `Bearer ${token}`);
	headers.set('Accept-Language', customerStoreBridge.getLanguage());

	const response = await fetch(`${import.meta.env.VITE_API_URL}${path}`, { ...init, headers });
	if (response.status === 401 && customerStoreBridge.isAuthenticated()) {
		customerStoreBridge.onLogout();
	}
	return response;
}

/**
 * Public fetch for customer portal endpoints that don't require authentication (e.g., company branding).
 */
export async function customerPublicFetch(path: string, init: RequestInit = {}): Promise<Response> {
	const headers = new Headers(init.headers);
	headers.set('Accept-Language', customerStoreBridge.getLanguage());
	return fetch(`${import.meta.env.VITE_API_URL}${path}`, { ...init, headers });
}
